/* ==========================================================================
   CUCU — bundle tier ladder engine
   --------------------------------------------------------------------------
   <cucu-bundle> renders a quantity ladder driven by cart.item_count.

   Why count and not spend: the tiers behind this are AUTOMATIC quantity
   discounts in Shopify admin (3+ free shipping / 4+ 15% / 6+ 20%). A tier
   with pct 0 is the free-shipping rung and is named that way in the copy. Because they're automatic, the
   discount is already applied in cart.js and at checkout no matter where the
   shopper added from — there is no code to paste and nothing to go and
   "finish" on a separate page. This component only has to narrate what
   Shopify is already doing, so the number on screen can never disagree with
   the number they're charged.

   Attributes (all written by snippets/cucu-bundle-ladder.liquid):
     data-tiers     JSON [{qty,pct,label,each?}] ascending by qty
     data-variant   "inline" (default) | "sticky"
     data-count     SSR cart.item_count, so first paint is correct
     data-currency  ISO code for Intl fallback
     data-money-fmt Shopify money format string, e.g. "${{amount}}"

   Every instance on the page registers into one module-level list and is
   driven by a single debounced /cart.js fetch, so the drawer, the cart page
   and the sticky bar can never disagree with each other.
   ========================================================================== */
(function () {
  'use strict';
  if (window.__cuBundle) return;
  window.__cuBundle = 1;

  var CART_RE = /\/cart\/(add|change|update|clear)(\.js|\.json)/;
  var DISMISS_KEY = 'cucu_bundle_bar_dismissed';
  var instances = [];
  var timer = null;
  var inFlight = false;
  var queued = false;

  /* ---------------------------------------------------------------- utils */

  function money(cents, el) {
    var fmt = el && el.getAttribute('data-money-fmt');
    var amount = (cents / 100).toFixed(2);
    if (fmt && fmt.indexOf('{{') !== -1) {
      // Shopify money formats: {{amount}}, {{amount_no_decimals}},
      // {{amount_with_comma_separator}}, {{amount_no_decimals_with_comma_separator}}
      var withCommas = function (s, sep) {
        var p = s.split('.');
        p[0] = p[0].replace(/\B(?=(\d{3})+(?!\d))/g, sep === ',' ? '.' : ',');
        return sep === ',' ? p.join(',') : p.join('.');
      };
      return fmt
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, withCommas(Math.round(cents / 100).toFixed(2), ',').replace(/,00$/, ''))
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withCommas(amount, ','))
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(cents / 100)))
        .replace(/\{\{\s*amount\s*\}\}/g, withCommas(amount, '.'));
    }
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: (el && el.getAttribute('data-currency')) || 'USD'
      }).format(cents / 100);
    } catch (e) {
      return '$' + amount;
    }
  }

  /* CUCU writes prices short — $9, not $9.00 — unless the cents matter. */
  function moneyShort(cents, el) {
    var out = money(cents, el);
    return out.replace(/([.,])00\b/, '');
  }

  function reduceMotion() {
    try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { return false; }
  }

  /* "Saved $X" counts up from the previous amount — the saving feels earned.
     First paint and reduced-motion just set the number. */
  function countUp(el, from, to, host, instant) {
    if (instant || reduceMotion() || from === to) { el.textContent = 'Saved ' + moneyShort(to, host); return; }
    var t0 = null, dur = 650;
    if (el._cuRaf) cancelAnimationFrame(el._cuRaf);
    function step(t) {
      if (!t0) t0 = t;
      var k = Math.min(1, (t - t0) / dur);
      var e = 1 - Math.pow(1 - k, 3);
      var v = Math.round((from + (to - from) * e) / 100) * 100;
      el.textContent = 'Saved ' + moneyShort(k === 1 ? to : v, host);
      if (k < 1) el._cuRaf = requestAnimationFrame(step);
    }
    el._cuRaf = requestAnimationFrame(step);
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function plural(n, one, many) {
    return n === 1 ? one : many;
  }

  /* Small numbers read as words in running copy. "2 more" in the middle of a
     sentence is a spreadsheet leaking into prose. */
  var WORDS = ['zero', 'one', 'two', 'three', 'four', 'five',
               'six', 'seven', 'eight', 'nine', 'ten'];
  function numWord(n) {
    return n >= 0 && n <= 10 ? WORDS[n] : String(n);
  }

  /* Equal-width segments between tiers, so each step feels equally close. */
  function fillPct(count, tiers) {
    var n = tiers.length, lo = 0;
    for (var i = 0; i < n; i++) {
      var hi = tiers[i].qty;
      if (count < hi) {
        var within = hi > lo ? (count - lo) / (hi - lo) : 1;
        return Math.max(0, Math.min(100, ((i + within) / n) * 100));
      }
      lo = hi;
    }
    return 100;
  }

  /* ---------------------------------------------------------------- sync  */

  function fetchCart() {
    return fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then(function (r) { return r.ok ? r.json() : null; }).catch(function () { return null; });
  }

  function sync() {
    if (inFlight) { queued = true; return; }
    inFlight = true;
    fetchCart().then(function (cart) {
      inFlight = false;
      if (cart) {
        instances.forEach(function (el) { el.render(cart); });
      }
      if (queued) { queued = false; schedule(); }
    });
  }

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(sync, 120);
  }

  ['cart:update', 'shopify:cart:lines-update', 'shopify:cart:attributes-update', 'cart:refresh']
    .forEach(function (ev) { document.addEventListener(ev, schedule); });

  // Theme code and apps both mutate the cart over AJAX without always firing a
  // documented event, so watch the network too. Chained, never replaced.
  var _fetch = window.fetch;
  if (typeof _fetch === 'function') {
    window.fetch = function (input) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      if (CART_RE.test(url)) {
        var out = _fetch.apply(this, arguments);
        var isAdd = /\/cart\/add/.test(url);
        out.then(function (res) {
          schedule();
          if (isAdd && res && res.ok) onAdded();
        }).catch(function () {});
        return out;
      }
      return _fetch.apply(this, arguments);
    };
  }
  /* ------------------------------------------------ add-to-cart feedback */
  /* Remember what was tapped; animate only once the add actually succeeded. */
  var pending = null;
  document.addEventListener('click', function (e) {
    var t = e.target && e.target.closest ? e.target : null;
    if (!t) return;
    var atc = t.closest('[data-cu-atc]');
    var up = t.closest('.cu-up__add');
    if (!atc && !up) return;
    var img = null;
    if (atc) {
      var sec = atc.closest('section, .shopify-section') || document;
      img = sec.querySelector('.cu-cardimg') || sec.querySelector('img');
    } else {
      var card = up.closest('.cu-up__card');
      img = card && card.querySelector('img');
    }
    pending = { btn: up || null, img: img, at: Date.now() };
  }, true);

  function onAdded() {
    var p = pending; pending = null;
    if (!p || Date.now() - p.at > 10000) return;
    if (p.btn) {
      /* upsell: the button confirms in place; the drawer re-render then
         moves the design into the cart list */
      p.btn.classList.add('is-added');
      var label = p.btn.firstChild && p.btn.firstChild.nodeType === 3 ? p.btn.firstChild : null;
      if (label) label.nodeValue = 'Added \u2713';
      return;
    }
    flyToCart(p.img);
  }

  function flyToCart(img) {
    if (!img || reduceMotion() || !img.animate) return;
    var icon = document.querySelector('.header-actions__cart-icon, [aria-controls="cart-drawer"], a[href="/cart"]');
    if (!icon) return;
    var a = img.getBoundingClientRect(), b = icon.getBoundingClientRect();
    if (!a.width || !b.width) return;
    var f = document.createElement('img');
    f.src = img.currentSrc || img.src;
    f.alt = '';
    f.className = 'cu-fly';
    f.style.cssText = 'position:fixed;z-index:9999;pointer-events:none;border-radius:10px;object-fit:contain;left:' +
      a.left + 'px;top:' + a.top + 'px;width:' + a.width + 'px;height:' + a.height + 'px;';
    document.body.appendChild(f);
    var dx = (b.left + b.width / 2) - (a.left + a.width / 2);
    var dy = (b.top + b.height / 2) - (a.top + a.height / 2);
    var anim = f.animate([
      { transform: 'translate(0,0) scale(1)', opacity: 1 },
      { transform: 'translate(' + dx * 0.55 + 'px,' + (dy * 0.55 - 60) + 'px) scale(.45)', opacity: 1, offset: 0.55 },
      { transform: 'translate(' + dx + 'px,' + dy + 'px) scale(.08)', opacity: 0.2 }
    ], { duration: 700, easing: 'cubic-bezier(.5,0,.3,1)' });
    anim.onfinish = function () {
      f.remove();
      if (icon.animate) icon.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.25)' }, { transform: 'scale(1)' }], { duration: 320, easing: 'ease-out' });
    };
  }

  var _open = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (CART_RE.test(String(url || ''))) this.addEventListener('load', schedule);
    return _open.apply(this, arguments);
  };

  /* ----------------------------------------------------------------- gift */

  /* A gift line is any line carrying the _gift property. It is free, so it must
     never count toward a quantity tier — otherwise a 4-cover cart plus a
     giveaway would trip the 5-cover discount and take 25% off the covers on the
     strength of a free gift. */
  function giftLines(cart) {
    return (cart.items || []).filter(function (i) {
      return i.properties && i.properties._gift;
    });
  }

  var giftBusy = false;

  /* Gift add/remove goes through Shopify.actions.updateCart when it exists.
     That's the storefront API Horizon listens to: it emits
     shopify:cart:lines-update from the drawer, so the drawer + cart page
     re-render with the new line and the header count updates. A raw
     /cart/add.js call is invisible to Horizon — the gift was added but the
     drawer never showed it. The fetch path stays as a fallback. */
  function viaActions() {
    return window.Shopify && Shopify.actions && Shopify.actions.updateCart ? Shopify.actions.updateCart : null;
  }

  function addGift(variantId) {
    if (giftBusy) return;
    giftBusy = true;
    var done = function () { giftBusy = false; schedule(); };
    var act = viaActions();
    var p = act
      ? act({ lines: [{ merchandiseId: String(variantId), quantity: 1, attributes: [{ key: '_gift', value: 'Free mystery cover' }] }] })
      : fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1, properties: { _gift: 'Free mystery cover' } }] })
        });
    p.then(done, done);
  }

  function dropGift(line) {
    if (giftBusy) return;
    giftBusy = true;
    var done = function () { giftBusy = false; schedule(); };
    var act = viaActions();
    var p = act
      ? act({ lines: [{ id: line.key, quantity: 0 }] })
      : fetch('/cart/change.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: line.key, quantity: 0 })
        });
    p.then(done, done);
  }

  /* ------------------------------------------------------------- element  */

  class CucuBundleEl extends HTMLElement {
    connectedCallback() {
      if (this._booted) return;
      this._booted = true;

      try { this.tiers = JSON.parse(this.getAttribute('data-tiers') || '[]'); }
      catch (e) { this.tiers = []; }
      if (!this.tiers.length) return;

      try { this.hooks = JSON.parse(this.getAttribute('data-hooks') || '[]'); }
      catch (e) { this.hooks = []; }

      this.variant = this.getAttribute('data-variant') || 'inline';
      this.max = this.tiers[this.tiers.length - 1].qty;
      this._prev = parseInt(this.getAttribute('data-count') || '0', 10) || 0;

      this.statusEl = this.querySelector('[data-status]');
      this.subStatusEl = this.querySelector('[data-substatus]');
      this.fillEl = this.querySelector('[data-fill]');
      this.trackEl = this.querySelector('[data-track]');
      this.pipEls = Array.prototype.slice.call(this.querySelectorAll('[data-pip]'));
      this.tierEls = Array.prototype.slice.call(this.querySelectorAll('[data-tier]'));
      this.footEl = this.querySelector('[data-foot]');
      this.savedEl = this.querySelector('[data-saved]');
      this.eachEl = this.querySelector('[data-each]');

      this.giftQty = parseInt(this.getAttribute('data-gift-qty') || '0', 10) || 0;
      this.giftEl = this.querySelector('[data-gift]');
      this.giftGridEl = this.querySelector('[data-gift-grid]');
      this.giftAddedEl = this.querySelector('[data-gift-added]');
      this.giftNameEl = this.querySelector('[data-gift-name]');

      if (this.giftEl) {
        this.giftEl.addEventListener('click', function (e) {
          var pick = e.target.closest('[data-gift-add]');
          if (pick) { addGift(pick.getAttribute('data-gift-add')); return; }
          if (e.target.closest('[data-gift-change]') && this._giftLine) dropGift(this._giftLine);
        }.bind(this));
      }

      /* The reminder bar should open the side cart, not bounce the shopper to
         a full page load of /cart. Left as a real <a href="/cart"> in the
         markup so it still works without JS and on the cart template, where
         <theme-drawer id="cart-drawer"> is not rendered at all. */
      /* In the sticky variant the CTA and the close button are SIBLINGS of
         <cucu-bundle>, not descendants — they live beside it in .cu-bd__inner.
         Querying from `this` found neither, which is why the bar's button fell
         through to a page load and the dismiss did nothing. */
      var host = this.closest('.cu-bd-sticky') || this;
      var cta = host.querySelector('.cu-bd__cta');
      if (cta) {
        cta.addEventListener('click', function (e) {
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;

          // Decide on the ELEMENT's existence, not on its method: cart-drawer.js
          // is type="module" fetchpriority="low", so on a cold page <theme-drawer>
          // has not upgraded yet and .open is still undefined. Testing for the
          // method here is what let the first version fall through to /cart.
          var drawer = document.getElementById('cart-drawer');
          if (!drawer) return; // cart template renders no drawer — let the link work
          e.preventDefault();

          var href = cta.getAttribute('href');
          var done = false;
          var give_up = setTimeout(function () {
            if (!done) { done = true; window.location.href = href; }
          }, 1200);

          customElements.whenDefined('theme-drawer').then(function () {
            if (done) return;
            done = true;
            clearTimeout(give_up);
            var d = document.getElementById('cart-drawer');
            try {
              if (d && typeof d.open === 'function') { d.open(); return; }
            } catch (err) { /* fall through */ }
            window.location.href = href;
          });
        });
      }

      var close = host.querySelector('[data-close]');
      if (close) {
        close.addEventListener('click', function () {
          try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch (e) {}
          // `is-up` lives on the .cu-bd-sticky wrapper, never on <cucu-bundle> —
          // the CSS has no rule for cucu-bundle.is-up at all. Taking it off the
          // wrapper also releases the reserved body padding, because that rule
          // is html:has(.cu-bd-sticky.is-up).
          host.classList.remove('is-up');
          this.hidden = true;
          document.documentElement.style.setProperty('--cu-bd-sticky-h', '0px');
        }.bind(this));
      }

      instances.push(this);
      schedule();
    }

    disconnectedCallback() {
      var i = instances.indexOf(this);
      if (i > -1) instances.splice(i, 1);
    }

    render(cart) {
      var gifts = giftLines(cart);
      var giftUnits = gifts.reduce(function (n, i) { return n + i.quantity; }, 0);
      var count = Math.max(0, (cart.item_count || 0) - giftUnits);
      var prev = this._prev;
      var tiers = this.tiers;
      var self = this;

      // The sticky bar is a nudge, not furniture: it stays out of the way
      // until there is something in the cart worth nudging about.
      if (this.variant === 'sticky') {
        var dismissed = false;
        try { dismissed = sessionStorage.getItem(DISMISS_KEY) === '1'; } catch (e) {}
        var show = count > 0 && !dismissed;
        this.hidden = !show;
        var host = this.closest('.cu-bd-sticky') || this;
        host.classList.toggle('is-up', show);
        document.documentElement.style.setProperty('--cu-bd-sticky-h', show ? (host.offsetHeight || 64) + 'px' : '0px');
        if (!show) { this._prev = count; return; }
      }

      /* progress */
      var pct = fillPct(count, tiers);
      if (this.fillEl) this.fillEl.style.width = pct + '%';
      if (this.trackEl) {
        this.trackEl.setAttribute('aria-valuenow', String(count));
        this.trackEl.setAttribute('aria-valuetext', count + ' of ' + this.max + ' items');
      }

      /* pips + tier cards */
      var nextIndex = -1;
      for (var j = 0; j < tiers.length; j++) {
        if (count < tiers[j].qty) { nextIndex = j; break; }
      }

      tiers.forEach(function (tier, i) {
        var on = count >= tier.qty;
        var justOn = on && prev < tier.qty;

        var pip = self.pipEls[i];
        if (pip) {
          pip.classList.toggle('is-on', on);
          if (justOn) {
            pip.classList.add('just-on');
            setTimeout(function () { pip.classList.remove('just-on'); }, 700);
          }
        }
        var card = self.tierEls[i];
        if (card) {
          card.classList.toggle('is-on', on);
          card.classList.toggle('is-next', i === nextIndex);
          if (justOn) {
            card.classList.add('just-on');
            setTimeout(function () { card.classList.remove('just-on'); }, 700);
          }
          var sr = card.querySelector('[data-sr]');
          if (sr) sr.textContent = on ? 'Unlocked: ' : 'Locked: ';
        }

      });

      /* status copy */
      /* Two lines: what they have, then the next step. Percentages, never a
         per-item price — a percentage reads as "applies to the whole order",
         which is true, while a unit price implies covers only and hands the
         shopper a number to go price-match elsewhere. The job here is to keep
         somebody moving toward checkout, not to open a comparison tab. */
      var self2 = this;
      var top = tiers[tiers.length - 1];
      var curTier = nextIndex === -1 ? top : (nextIndex > 0 ? tiers[nextIndex - 1] : null);

      function reward(t) { return t.pct > 0 ? t.pct + '% off' : 'free shipping'; }
      var giftOn = !!(self2.giftQty && self2.giftEl);

      function perk() {
        var out = '';
        // Once the gift is earned it replaces the shipping note — the headline
        // stays one line, and free shipping is already implied by that rung.
        if (giftOn && count >= self2.giftQty) return ' + a free mystery cover';
        if (curTier && curTier.pct > 0 && curTier.label && curTier.label.charAt(0) === '+') out += ' ' + curTier.label;
        return out;
      }
      function curHead() {
        return curTier.pct > 0
          ? 'You\u2019re saving ' + curTier.pct + '%' + perk()
          : 'You\u2019ve unlocked free shipping' + perk();
      }

      var hook = '';
      var sub = '';

      if (!count) {
        hook = 'Add ' + numWord(tiers[0].qty) + ' items for ' + reward(tiers[0]);
        sub = 'The more you add, the more you get.';
      } else if (!curTier) {
        hook = 'Add ' + numWord(tiers[0].qty - count) + ' more for ' + reward(tiers[0]);
        sub = tiers.length > 1
          ? 'Then ' + reward(tiers[1]) + ' at ' + tiers[1].qty + ' items.'
          : 'It applies to your whole order.';
      } else if (nextIndex > -1) {
        hook = curHead();
        sub = 'Add ' + numWord(tiers[nextIndex].qty - count) + ' more to get ' + reward(tiers[nextIndex]) +
          (giftOn && tiers[nextIndex].qty === self2.giftQty ? ' + a free mystery cover' : '');
      } else {
        hook = curHead();
        sub = 'That\u2019s the best deal we do.';
      }

      if (this.statusEl) {
        this.statusEl.innerHTML = nextIndex === -1 && count
          ? '<span class="cu-bd__done"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 6L9 17l-5-5"/></svg>' + esc(hook) + '</span>'
          : esc(hook);
      }
      if (this.subStatusEl) this.subStatusEl.textContent = sub;

      /* headline changed (not first paint) -> soft fade-up so the shopper's eye
         catches the new state */
      if (this._hook !== undefined && this._hook !== hook && this.statusEl && !reduceMotion()) {
        var st = this.statusEl;
        st.classList.remove('is-new'); void st.offsetWidth; st.classList.add('is-new');
        setTimeout(function () { st.classList.remove('is-new'); }, 500);
      }
      this._hook = hook;

      /* savings footer — reads the discount Shopify actually applied */
      if (this.footEl) {
        var saved = cart.total_discount || 0;
        var show2 = count > 0 && saved > 0;
        this.footEl.hidden = !show2;
        if (show2) {
          if (this.savedEl) countUp(this.savedEl, this._saved || 0, saved, this, this._saved === undefined);
          this._saved = saved;
          /* Deliberately NOT a per-item price. A unit price invites a price
             comparison somewhere else and implies the discount only covers one
             product type; this says the opposite, which is also the truth —
             the tiers are storewide. */
          if (this.eachEl) this.eachEl.textContent = 'On everything in your cart';
        }
      }

      /* gift picker */
      if (this.giftEl && this.giftQty > 0) {
        var has = gifts.length > 0;
        this._giftLine = has ? gifts[0] : null;

        if (has && count < this.giftQty) {
          // They dropped below the threshold. Take the gift back here rather
          // than let checkout quietly charge them for it.
          dropGift(gifts[0]);
        } else {
          var earned = count >= this.giftQty;
          this.giftEl.hidden = !earned;
          /* first time the gift unlocks (not on page load): flip the mystery card in */
          if (earned && this._earned === false && !reduceMotion()) {
            var gEl = this.giftEl;
            gEl.classList.remove('is-reveal'); void gEl.offsetWidth; gEl.classList.add('is-reveal');
            setTimeout(function () { gEl.classList.remove('is-reveal'); }, 1100);
          }
          this._earned = earned;
          if (earned) {
            if (this.giftAddedEl) this.giftAddedEl.hidden = !has;
            if (this.giftGridEl) this.giftGridEl.hidden = has;
            if (has && this.giftNameEl) this.giftNameEl.textContent = gifts[0].variant_title || 'your size';
          }
        }
      }

      this._prev = count;
    }
  }

  if (!customElements.get('cucu-bundle')) customElements.define('cucu-bundle', CucuBundleEl);

  /* Sticky bar CTA opens the cart drawer in place instead of navigating to
     /cart. Clarity (Sep 22–24): shoppers who added to cart and didn't check
     out mostly left from collection/search pages — keep them one tap from
     the drawer without losing their place in the grid. */
  document.addEventListener('click', function (e) {
    var a = e.target && e.target.closest ? e.target.closest('.cu-bd__cta') : null;
    if (!a) return;
    var oc = window.Shopify && Shopify.actions && Shopify.actions.openCart;
    if (!oc) return;
    e.preventDefault();
    try { oc(); } catch (err) { window.location.href = a.href; }
  });
})();
